import React, { FC } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { UnbrandedLoader } from '@modernloop/shared/helper-components';
import { Alert, Box, Divider, Stack, TextField, Typography } from '@mui/material';

import { useDisconnectAtsDialogQuery } from 'src/generated/mloop-graphql';

import WarningIllustration from 'src/assets/images/illustrations/warning_64.png';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DisconectAtsDialogQuery = gql`
  query DisconnectAtsDialog {
    thisOrg {
      upcomingInterviewsCount
      activeCandidateAvailabilityLinksCount
      activeSelfScheduleLinksCount
    }
  }
`;
interface Props {
  integrationName: string;
  onClose: () => void;
  onRemove: () => void;
}

export const DisconectAtsDialog: FC<Props> = ({ integrationName, onClose, onRemove }) => {
  const { t } = useTranslation();
  const { data, loading } = useDisconnectAtsDialogQuery();
  const [disconnectEnabled, setDisconnectEnabled] = React.useState<boolean>(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.trim() === 'DISCONNECT') {
      setDisconnectEnabled(true);
    } else {
      setDisconnectEnabled(false);
    }
  };

  const activeSelfScheduleLinksCount = data?.thisOrg?.activeSelfScheduleLinksCount;
  const activeCandidateAvailabilityLinksCount = data?.thisOrg?.activeCandidateAvailabilityLinksCount;
  const upcomingInterviewsCount = data?.thisOrg?.upcomingInterviewsCount;

  return (
    <Dialog
      decoration={
        <img
          src={WarningIllustration}
          alt={t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.disconnectAtsAlt')}
          height="64px"
          width="64px"
        />
      }
      maxWidth="sm"
      open
      onClose={onClose}
      aria-labelledby="confirm-dialog"
      title={t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.disconnectTitle', {
        integrationName,
      })}
      subTitle={t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.cannotBeUndone')}
      submitOptions={{
        label: t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.disconnectButton', {
          integrationName,
        }),
        onClick: onRemove,
        isDangerous: true,
        isDisabled: !disconnectEnabled,
      }}
      cancelOptions={{
        label: t('hiring-dashboard-common:common.cancel'),
        onClick: onClose,
      }}
    >
      {loading && <UnbrandedLoader />}
      <Stack divider={<Divider />} gap={1}>
        <Stack gap={1}>
          <Typography variant="subtitle1">
            {t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.whatWillHappen')}
          </Typography>
          <Typography>
            {t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.openJobsClosed')}
          </Typography>
        </Stack>
        <Typography>
          {t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.activeAppsArchived')}
        </Typography>
        <Typography>
          {t(
            'hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.employeesDisassociated',
            { integrationName }
          )}
        </Typography>
        <Typography>
          {t(
            'hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.upcomingInterviewsHidden'
          )}
        </Typography>
        {activeSelfScheduleLinksCount && (
          <Typography>
            {t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.selfSchedulesClosed', {
              count: activeSelfScheduleLinksCount,
            })}
          </Typography>
        )}
        {activeCandidateAvailabilityLinksCount && (
          <Typography>
            {t(
              'hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.availabilityRequestsClosed',
              {
                count: activeCandidateAvailabilityLinksCount,
              }
            )}
          </Typography>
        )}
        {upcomingInterviewsCount && (
          <Typography>
            {t(
              'hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.upcomingInterviewsNotEditable',
              {
                count: upcomingInterviewsCount,
              }
            )}
          </Typography>
        )}
      </Stack>
      <Box mt={1}>
        <Alert severity="warning" icon={false}>
          <Stack gap={1}>
            <Typography variant="subtitle1">
              {t('hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.typeDisconnect')}
            </Typography>
            <TextField
              fullWidth
              placeholder={t(
                'hiring-dashboard-settings-integrations:settings.integrations.disconnectAtsDialog.typeDisconnectPlaceholder'
              )}
              onChange={handleInputChange}
            />
          </Stack>
        </Alert>
      </Box>
    </Dialog>
  );
};
